/* eslint-disable function-paren-newline */
import { useEffect, useRef } from 'react';

import useInterval from '@use-it/interval';
import { StatesInCollection } from '../../hooks/useStateCollection/interfaces/stateInCollection.interface';
import { ContentValidatorHelper } from './ContentValidatorHelper';
import { IValidatorContext } from './interfaces/validatorContext.interface';
import PatternsValidator from './validators/patterns/PatternsValidator';
import WordCountValidator from './validators/wordCount/wordCount.validator';
import { ImageValidator } from './validators/image/image.validator';
import { IValidationResult } from './interfaces/validation.interface';

export const createValidatorContext = (
  states: StatesInCollection,
): IValidatorContext => {
  return {
    content: states.content.value[0],
    wordCount: states.wordCount.value[0],
    htmlNodes: states.htmlNodes.value,
    tag: states.tag.value[0],
    onAnalysisChange: states.onAnalysisChange.value,
    countries: states.countries.value[0],
    articleType: states.articleType.value[0],
    siteDomain: states.siteDomain.value,
  };
};

export class ContentValidator {
  constructor(public context: IValidatorContext) {
    console.log('new ContentValidator');
  }

  private wordCountValidator = new WordCountValidator(
    this.context.wordCount,
    this.context.tag,
    this.context.countries,
  );

  private patternsValidator = new PatternsValidator(
    this.context.htmlNodes,
    this.context.tag,
    this.context.articleType,
  );

  private imageValidator = new ImageValidator(
    this.context.htmlNodes,
    this.context.siteDomain,
  );

  validate(): IValidationResult {
    const wordCountValidation = this.wordCountValidator.validate();
    const patternsValidation = this.patternsValidator.validate();
    const imageValidation = this.imageValidator.validate();

    return ContentValidatorHelper.mergeValidations([
      wordCountValidation,
      patternsValidation,
      imageValidation,
    ]);
  }
}

export const useContentValidator = (
  context: IValidatorContext,
): (() => IValidationResult) => {
  const VALIDATION_INTERVAL = 3000;

  const contentValidator = useRef<ContentValidator | null>(null);

  const firstRun = useRef(true);
  const lastContent = useRef(context.content);
  const { onAnalysisChange } = context;

  const validate = () => {
    contentValidator.current = new ContentValidator(context);
    const validationResult = contentValidator.current.validate();
    return validationResult;
  };

  useInterval(() => {
    if (context.content !== lastContent.current) {
      lastContent.current = context.content;

      contentValidator.current = new ContentValidator(context);
      const validationResult = contentValidator.current.validate();
      console.log('validationResult', validationResult);
      onAnalysisChange(validationResult);
    }
  }, VALIDATION_INTERVAL);

  useEffect(() => {
    if (context.htmlNodes && firstRun.current) {
      contentValidator.current = new ContentValidator(context);
      firstRun.current = false;
      const validationResult = contentValidator.current.validate();
      console.log(validationResult);
      onAnalysisChange(validationResult);
    }
  }, [context, context.content, context.htmlNodes, onAnalysisChange]);

  return validate;
};
